// Text of the conversational Copilot flow (welcome, Common questions, guided support request) in
// Arabic and English. Wording of the assistant's questions follows the approved copy; functions take
// the customer's first name where the assistant addresses them.

export const GUIDED_TEXT = {
  en: {
    welcomeTitle: "Welcome to LUD! 👋",
    welcomeBody: "We're happy to help. What can we assist you with today?",
    commonQuestions: "Common Questions",
    contactSupport: "Contact Support",
    myConversations: "My Conversations",
    continueRequest: "Continue your request",
    chatLog: "Conversation with LUD Assistant",
    assistantName: "LUD Assistant",
    youName: "You",

    // FAQ
    faqTopicsPrompt: "Sure! Pick a topic and I'll show you the most common questions.",
    faqQuestionsPrompt: (topic) => `Here are the common questions about “${topic}”. Pick one to see the answer.`,
    faqLoading: "Loading the common questions…",
    faqLoadError: "We couldn't load the common questions right now.",
    faqAnswerError: "We couldn't load this answer.",
    faqEmpty: "There are no common questions available right now. You can contact our team instead.",
    otherTopics: "Other topics",
    anotherQuestion: "Another question",
    didItHelp: "Did this answer your question?",
    yesHelped: "Yes, that helped",
    noNeedHelp: "No, I need help",
    helpfulClosing: "I'm glad that helped! 😊 If anything else comes up, I'm right here whenever you need me.",
    backToStart: "Back to the start",
    escalateLead: "I'm sorry that didn't solve it. Let's get you to our team. I've kept your question, so there's no need to repeat it.",
    yourQuestion: "Your question",
    showAnswer: "Show the answer you saw",
    hideAnswer: "Hide the answer",

    // guided intake
    categoryPrompt: "To help you better, what do you need assistance with?",
    categoryPrefilled: (category) => `I'll file this under “${category}”. You can change it before sending.`,
    messagePrompt: "Thank you. Could you describe your question or the issue you're experiencing? You can also attach a screenshot to help us understand it better.",
    messagePromptFaq: "Thanks for letting us know. What is still unclear, or what isn't working for you? You can also attach a screenshot.",
    namePrompt: "Before we continue, what name would you like us to use?",
    emailPrompt: (name) => `Thank you, ${name}. What email address should we use to send you updates and our response?`,
    emailPromptNoName: "What email address should we use to send you updates and our response?",
    phonePrompt: (name) => (name ? `One last detail, ${name}. What phone number can our team use if we need to contact you?` : "One last detail. What phone number can our team use if we need to contact you?"),
    reviewIntro: "Here's a summary of your request. Please check it before sending.",
    editWhich: "Which detail would you like to change?",
    fieldCategory: "Category",
    fieldMessage: "Your message",
    fieldName: "Name",
    fieldEmail: "Email",
    fieldPhone: "Phone",
    fieldFaq: "Related question",
    fieldRestaurant: "Restaurant",
    restaurantPrompt: "Which restaurant is this about?",
    noRestaurant: "Not about a specific restaurant",
    sendRequest: "Send Request",
    editDetails: "Edit Details",
    sendingRequest: "Sending your request…",
    back: "Back",
    cancelRequest: "Cancel",
    discardQuestion: "Do you want to discard this request and go back to the start?",
    discardYes: "Yes, discard it",
    keepGoing: "No, keep going",
    send: "Send",
    next: "Next",
    attach: "Attach a file",
    removeFile: "Remove",
    messagePlaceholder: "Type your message…",
    namePlaceholder: "Your name",
    emailPlaceholder: "name@example.com",
    phonePlaceholder: "05XXXXXXXX or +country code",
    emailHint: "We use it to send you updates. We don't treat it as proof of identity.",
    phoneHint: "Saudi numbers (05XXXXXXXX) and international numbers (+…) are accepted.",
    answerLabel: "Your answer",

    // friendly validation
    errMessageEmpty: "Please write a few words about your question or issue so we can help.",
    errMessageLong: (max) => `That's a bit long. Please keep it under ${max} characters.`,
    errName: "Could you enter your name? It should have at least 2 letters.",
    errEmail: "That email address doesn't look quite right. Could you check it and try again?",
    errPhone: "That phone number doesn't look right. Please enter a Saudi number like 05XXXXXXXX, or an international number starting with + and the country code.",
    errCategory: "Please choose one of the options above.",
    submitFailed: "We couldn't send your request just now, and nothing was sent twice. Please try again.",
    submitNoConnection: "It looks like you're offline. Check your connection and try again.",
    tryAgain: "Try again",

    // confirmation
    confirmTitle: (name) => (name ? `Thank you, ${name}! 🌷` : "Thank you! 🌷"),
    confirmReceived: (reference) => `Your support request has been received successfully. Your reference number is ${reference}.`,
    confirmNext: "Our LUD Support team will review the details and respond as soon as possible. You can follow the conversation here, and we'll notify you by email when a response is available.",
    confirmReturn: "You can return to this conversation to check for updates or share additional information.",
    confirmTrust: "We appreciate your trust in LUD.",
    confirmGuest: "On this device and browser you can come back to this conversation for 14 days after your last message. Keep your reference number; the link in our email also opens it.",
    confirmNoAccess: "This request was already received. To protect your privacy the conversation can only be reopened from the link in our email.",
    openConversation: "Open the conversation",
    attachmentsNote: "Your attachments are being added to the conversation.",
  },
  ar: {
    welcomeTitle: "مرحبًا بك في لُد 👋",
    welcomeBody: "يسعدنا مساعدتك! كيف يمكننا خدمتك اليوم؟",
    commonQuestions: "الأسئلة الشائعة",
    contactSupport: "التواصل مع الدعم",
    myConversations: "محادثاتي",
    continueRequest: "متابعة طلبك",
    chatLog: "المحادثة مع مساعد لُد",
    assistantName: "مساعد لُد",
    youName: "أنت",

    faqTopicsPrompt: "بكل سرور! اختر موضوعًا وسأعرض لك أكثر الأسئلة شيوعًا.",
    faqQuestionsPrompt: (topic) => `هذه الأسئلة الشائعة حول «${topic}». اختر سؤالًا لعرض إجابته.`,
    faqLoading: "جارٍ تحميل الأسئلة الشائعة…",
    faqLoadError: "تعذّر تحميل الأسئلة الشائعة الآن.",
    faqAnswerError: "تعذّر تحميل هذه الإجابة.",
    faqEmpty: "لا تتوفر أسئلة شائعة حاليًا. يمكنك التواصل مع فريقنا مباشرة.",
    otherTopics: "مواضيع أخرى",
    anotherQuestion: "سؤال آخر",
    didItHelp: "هل أجابت هذه المعلومات عن استفسارك؟",
    yesHelped: "نعم، شكرًا",
    noNeedHelp: "لا، أحتاج مساعدة",
    helpfulClosing: "يسعدنا أن المعلومات كانت مفيدة 😊 وإذا احتجت إلى أي شيء آخر فنحن هنا في أي وقت.",
    backToStart: "العودة إلى البداية",
    escalateLead: "نأسف لأن الإجابة لم تحل المشكلة. لنوصلك إلى فريقنا؛ احتفظنا بسؤالك فلا حاجة إلى تكراره.",
    yourQuestion: "سؤالك",
    showAnswer: "عرض الإجابة التي ظهرت لك",
    hideAnswer: "إخفاء الإجابة",

    categoryPrompt: "حتى نتمكن من مساعدتك بشكل أفضل، ما نوع المساعدة التي تحتاجها؟",
    categoryPrefilled: (category) => `سنسجّل طلبك ضمن «${category}». يمكنك تغيير ذلك قبل الإرسال.`,
    messagePrompt: "شكرًا لك. هل يمكنك توضيح المشكلة أو الاستفسار الذي تواجهه؟ يمكنك أيضًا إرفاق صورة لتساعدنا على فهم طلبك بشكل أفضل.",
    messagePromptFaq: "شكرًا لإخبارنا. ما الذي ما زال غير واضح، أو ما الذي لا يعمل معك؟ يمكنك أيضًا إرفاق صورة.",
    namePrompt: "حتى نتمكن من التواصل معك بشكل مناسب، ما الاسم الذي تفضّل أن نناديك به؟",
    emailPrompt: (name) => `شكرًا لك يا ${name}. ما بريدك الإلكتروني الذي يمكننا إرسال الرد والتحديثات المتعلقة بطلبك إليه؟`,
    emailPromptNoName: "ما بريدك الإلكتروني الذي يمكننا إرسال الرد والتحديثات المتعلقة بطلبك إليه؟",
    phonePrompt: (name) => (name ? `بقيت خطوة أخيرة يا ${name}. ما رقم الجوال الذي يمكن لفريقنا التواصل معك من خلاله عند الحاجة؟` : "بقيت خطوة أخيرة. ما رقم الجوال الذي يمكن لفريقنا التواصل معك من خلاله عند الحاجة؟"),
    reviewIntro: "هذا ملخص طلبك. يُرجى مراجعته قبل الإرسال.",
    editWhich: "ما البيان الذي تريد تعديله؟",
    fieldCategory: "التصنيف",
    fieldMessage: "رسالتك",
    fieldName: "الاسم",
    fieldEmail: "البريد الإلكتروني",
    fieldPhone: "الجوال",
    fieldFaq: "السؤال المرتبط",
    fieldRestaurant: "المطعم",
    restaurantPrompt: "ما المطعم الذي يخصه الطلب؟",
    noRestaurant: "لا يخص مطعمًا محددًا",
    sendRequest: "إرسال الطلب",
    editDetails: "تعديل البيانات",
    sendingRequest: "جارٍ إرسال طلبك…",
    back: "رجوع",
    cancelRequest: "إلغاء",
    discardQuestion: "هل تريد إلغاء هذا الطلب والعودة إلى البداية؟",
    discardYes: "نعم، إلغاء الطلب",
    keepGoing: "لا، أكمل الطلب",
    send: "إرسال",
    next: "التالي",
    attach: "إرفاق ملف",
    removeFile: "إزالة",
    messagePlaceholder: "اكتب رسالتك…",
    namePlaceholder: "اسمك",
    emailPlaceholder: "name@example.com",
    phonePlaceholder: "05XXXXXXXX أو رقم دولي بالرمز +",
    emailHint: "نستخدمه لإرسال التحديثات إليك، ولا نعدّه إثباتًا للهوية.",
    phoneHint: "نقبل الأرقام السعودية (05XXXXXXXX) والأرقام الدولية (+…).",
    answerLabel: "إجابتك",

    errMessageEmpty: "اكتب لنا بضع كلمات عن استفسارك أو مشكلتك حتى نتمكن من مساعدتك.",
    errMessageLong: (max) => `الرسالة طويلة قليلًا. يُرجى ألا تتجاوز ${max} حرف.`,
    errName: "هل يمكنك كتابة اسمك؟ يجب أن يحتوي على حرفين على الأقل.",
    errEmail: "يبدو أن البريد الإلكتروني غير مكتمل. هل يمكنك التأكد منه وإعادة إرساله؟",
    errPhone: "يبدو أن رقم الجوال غير صحيح. أدخل رقمًا سعوديًا مثل 05XXXXXXXX، أو رقمًا دوليًا يبدأ بالرمز + ثم رمز الدولة.",
    errCategory: "يُرجى اختيار أحد الخيارات أعلاه.",
    submitFailed: "تعذّر إرسال طلبك الآن، ولم يُرسل شيء مرتين. يُرجى المحاولة مرة أخرى.",
    submitNoConnection: "يبدو أنك غير متصل بالإنترنت. تحقق من الاتصال وحاول مرة أخرى.",
    tryAgain: "إعادة المحاولة",

    confirmTitle: (name) => (name ? `شكرًا لك يا ${name} 🌷` : "شكرًا لك 🌷"),
    confirmReceived: (reference) => `تم استلام طلبك بنجاح، وتسجيله برقم ${reference}.`,
    confirmNext: "سيقوم فريق دعم لُد بمراجعة التفاصيل والرد عليك في أقرب وقت ممكن عبر هذه المحادثة، وسنرسل إليك إشعارًا على بريدك الإلكتروني عند توفر رد.",
    confirmReturn: "يمكنك العودة إلى هذه المحادثة في أي وقت لمتابعة طلبك أو إضافة معلومات جديدة.",
    confirmTrust: "نسعد بخدمتك، ونقدّر ثقتك بلُد.",
    confirmGuest: "يمكنك العودة إلى هذه المحادثة من هذا الجهاز وهذا المتصفح لمدة 14 يومًا بعد آخر رسالة. احتفظ برقم المرجع؛ كما يفتحها الرابط في رسالتنا الإلكترونية.",
    confirmNoAccess: "سبق استلام هذا الطلب. حفاظًا على خصوصيتك لا يمكن إعادة فتح المحادثة إلا من الرابط في رسالتنا الإلكترونية.",
    openConversation: "فتح المحادثة",
    attachmentsNote: "جارٍ إضافة مرفقاتك إلى المحادثة.",
  },
};
